import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { CalendarClock, Check, CornerDownRight, Minus, Palette, Plus, Scan } from 'lucide-react';
import { Button, IconButton, Menu } from '../../../../components/ui';
import { govDate, plural } from '../../format';
import {
  TIER_LABEL,
  TimelineColour,
  dayOf,
  goneSentence,
  lanesOf,
  legendOf,
  lengthOf,
  personOf,
  seatRows,
  spanOf,
  tenureMatches,
  ticksOf,
  todayDay,
  toneOf,
} from '../../timeline';
import type { GovActView, SeatView, TimelineView } from '../../types';
import { ACT_KIND, postLabel } from './TimelineActs';

export type TimelineSelection = { kind: 'tenure'; index: number } | { kind: 'act'; id: string } | null;

const LANE = 26;
const ROW_PAD = 7;
const SECTION = 30;
const AXIS = 58;
const MIN_PX = 0.04;
const MAX_PX = 6;
/** How much of the period a phone shows at first */
const PHONE_DAYS = 730;

const COLOURS: Array<{ value: TimelineColour; label: string }> = [
  { value: 'person', label: 'One colour per person' },
  { value: 'post', label: 'Kind of post' },
  { value: 'ending', label: 'How it ended' },
];

type Row =
  | { type: 'section'; tier: SeatView['tier']; top: number; height: number }
  | {
      type: 'seat';
      seat: SeatView;
      lane: number[];
      top: number;
      height: number;
    };

/**
 * The state over time: a row per post, each holder a bar from the act that
 * gave them the post to the act that ended it, the acts along the top. One
 * colour per person, so someone who moves is easy to follow — and chosen, a
 * curve joins their posts one to the next.
 */
export const TimelineChart: React.FC<{
  view: TimelineView;
  seats: SeatView[];
  query: string;
  selected: TimelineSelection;
  onSelect: (next: TimelineSelection) => void;
  /** Shown over the whole screen: the chart takes all the height it is given */
  expanded?: boolean;
}> = ({ view, seats, query, selected, onSelect, expanded = false }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [px, setPx] = useState<number | null>(null);
  const [colourBy, setColourBy] = useState<TimelineColour>('person');
  const [hovered, setHovered] = useState<{ index: number; x: number; y: number } | null>(null);
  const anchor = useRef<{ day: number; offset: number } | null>(null);
  const drag = useRef<{ x: number; y: number; left: number; top: number } | null>(null);

  const labelWidth = width && width < 640 ? 132 : 248;
  const span = useMemo(() => spanOf(view), [view]);
  const days = span.to - span.from;
  const fitPx = width ? Math.min(MAX_PX, Math.max(MIN_PX, (width - labelWidth - 16) / days)) : 0.3;
  const scale = px ?? fitPx;
  const trackWidth = Math.max(days * scale, 200);
  const xOf = useCallback((day: number) => (day - span.from) * scale, [span.from, scale]);
  const today = todayDay();

  /* ---- measuring ---- */
  useLayoutEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    const observer = new ResizeObserver(() => setWidth(node.clientWidth));
    observer.observe(node);
    setWidth(node.clientWidth);
    return () => observer.disconnect();
  }, []);

  /* ---- rows ---- */
  const rows = useMemo(() => {
    const list: Row[] = [];
    let top = 0;
    let tier: SeatView['tier'] | null = null;
    for (const seat of seats) {
      if (seat.tier !== tier) {
        tier = seat.tier;
        list.push({ type: 'section', tier, top, height: SECTION });
        top += SECTION;
      }
      // The post at the body today, and under it the bodies it replaced
      for (const row of seatRows(seat)) {
        const { lane, count } = lanesOf(row.tenures.map((index) => view.tenures[index]));
        const height = count * LANE + ROW_PAD * 2 - 4;
        list.push({ type: 'seat', seat: row, lane, top, height });
        top += height;
      }
    }
    return { list, height: top };
  }, [seats, view.tenures]);

  /** Where each tenure's bar sits, for the curves between one person's posts */
  const place = useMemo(() => {
    const map = new Map<number, { y: number; from: number; to: number }>();
    for (const row of rows.list) {
      if (row.type !== 'seat') continue;
      row.seat.tenures.forEach((index, position) => {
        const tenure = view.tenures[index];
        map.set(index, {
          y: row.top + ROW_PAD + row.lane[position] * LANE + (LANE - 6) / 2,
          from: dayOf(tenure.from),
          to: tenure.to ? dayOf(tenure.to) : today,
        });
      });
    }
    return map;
  }, [rows, view.tenures, today]);

  /* ---- what is lit ---- */
  const selectedTenure = selected?.kind === 'tenure' ? view.tenures[selected.index] : null;
  const selectedPerson = selectedTenure ? personOf(selectedTenure) : null;
  const selectedAct = selected?.kind === 'act' ? view.acts.find((act) => act.id === selected.id) : null;
  const inAct = useMemo(() => {
    if (!selectedAct) return null;
    const set = new Set<number>();
    view.tenures.forEach((tenure, index) => {
      if (
        tenure.fromAct.id === selectedAct.id ||
        tenure.toAct?.id === selectedAct.id ||
        tenure.confirmedBy.some((act) => act.id === selectedAct.id)
      )
        set.add(index);
    });
    return set;
  }, [selectedAct, view.tenures]);
  const seatOfIndex = useMemo(() => {
    const map = new Map<number, SeatView>();
    for (const seat of seats) for (const row of seatRows(seat)) for (const index of row.tenures) map.set(index, row);
    return map;
  }, [seats]);
  const searching = query.trim().length > 0;
  const lit = (index: number): boolean => {
    const tenure = view.tenures[index];
    if (selectedPerson) return personOf(tenure) === selectedPerson;
    if (inAct) return inAct.has(index);
    if (searching) return tenureMatches(tenure, seatOfIndex.get(index), query);
    return true;
  };
  const dimming = !!selectedPerson || !!inAct || searching;

  /** One person's posts in order, joined where one led to the next */
  const curves = useMemo(() => {
    if (!selectedPerson) return [];
    const mine = Array.from(place.entries())
      .filter(([index]) => personOf(view.tenures[index]) === selectedPerson)
      .map(([, spot]) => spot)
      .sort((a, b) => a.from - b.from);
    const list: Array<{ x1: number; y1: number; x2: number; y2: number }> = [];
    for (let i = 1; i < mine.length; i++) {
      const [a, b] = [mine[i - 1], mine[i]];
      if (Math.abs(a.y - b.y) < 2) continue;
      list.push({ x1: xOf(Math.min(a.to, b.from)), y1: a.y, x2: xOf(b.from), y2: b.y });
    }
    return list;
  }, [selectedPerson, place, view.tenures, xOf]);

  /* ---- zoom and pan ---- */
  const zoom = useCallback(
    (factor: number, clientX?: number) => {
      const node = scrollRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const offset =
        clientX !== undefined
          ? Math.max(0, clientX - rect.left - labelWidth)
          : (node.clientWidth - labelWidth) / 2;
      anchor.current = { day: span.from + (node.scrollLeft + offset) / scale, offset };
      setPx(Math.min(MAX_PX, Math.max(MIN_PX, scale * factor)));
    },
    [labelWidth, scale, span.from],
  );

  useLayoutEffect(() => {
    const node = scrollRef.current;
    if (!node || !anchor.current) return;
    node.scrollLeft = (anchor.current.day - span.from) * scale - anchor.current.offset;
    anchor.current = null;
  }, [scale, span.from]);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      zoom(Math.pow(0.998, event.deltaY), event.clientX);
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, [zoom]);

  const goTo = useCallback(
    (day: number, y?: number) => {
      const node = scrollRef.current;
      if (!node) return;
      const visible = node.clientWidth - labelWidth;
      node.scrollTo({
        left: Math.max(0, xOf(day) - visible * 0.35),
        top: y !== undefined ? Math.max(0, y + AXIS - node.clientHeight / 2) : node.scrollTop,
        behavior: 'smooth',
      });
    },
    [labelWidth, xOf],
  );

  // Open at today, the end the reader cares about first — on a phone, the
  // last two years rather than the whole period squeezed into the screen
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current || !width) return;
    opened.current = true;
    const node = scrollRef.current;
    if (!node) return;
    const visible = width - labelWidth;
    if (width < 640 && px === null && days > PHONE_DAYS) {
      anchor.current = { day: span.to, offset: visible };
      setPx(Math.max(MIN_PX, visible / PHONE_DAYS));
    } else {
      node.scrollLeft = node.scrollWidth;
    }
  }, [width, labelWidth, px, days, span.to]);

  // Something chosen elsewhere — in the seats, in an act — is brought into view
  useEffect(() => {
    if (selected?.kind !== 'tenure') return;
    const spot = place.get(selected.index);
    if (spot) goTo(spot.from, spot.y);
  }, [selected, place, goTo]);

  // A search settles on its first find
  useEffect(() => {
    if (!searching) return;
    const timer = setTimeout(() => {
      const first = Array.from(place.entries())
        .filter(([index]) => tenureMatches(view.tenures[index], seatOfIndex.get(index), query))
        .sort((a, b) => a[1].y - b[1].y)[0];
      if (first) goTo(first[1].from, first[1].y);
    }, 450);
    return () => clearTimeout(timer);
  }, [query, searching, place, view.tenures, seatOfIndex, goTo]);

  const onPointerDown = (event: React.PointerEvent) => {
    if (event.pointerType !== 'mouse' || (event.target as HTMLElement).closest('button')) return;
    const node = scrollRef.current;
    if (!node) return;
    drag.current = { x: event.clientX, y: event.clientY, left: node.scrollLeft, top: node.scrollTop };
  };
  const onPointerMove = (event: React.PointerEvent) => {
    const node = scrollRef.current;
    if (!drag.current || !node) return;
    node.scrollLeft = drag.current.left - (event.clientX - drag.current.x);
    node.scrollTop = drag.current.top - (event.clientY - drag.current.y);
  };
  const endDrag = () => {
    drag.current = null;
  };

  const ticks = useMemo(() => ticksOf(span.from, span.to, scale), [span, scale]);
  const legend = legendOf(colourBy);
  const inPost = view.tenures.filter((tenure) => !tenure.to).length;
  const hoveredTenure = hovered ? view.tenures[hovered.index] : null;

  return (
    <div
      className={`overflow-hidden rounded-2xl border border-neutral-200 bg-white ${
        expanded ? 'flex min-h-0 flex-1 flex-col' : ''
      }`}
    >
      {/* ---- the chart ---- */}
      <div className={expanded ? 'relative min-h-0 flex-1' : 'relative'}>
      <div
        ref={scrollRef}
        className={`relative overflow-auto overscroll-contain ${expanded ? 'h-full' : 'max-h-[70vh] min-h-[320px]'}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onScroll={() => hovered && setHovered(null)}
      >
        <div style={{ width: labelWidth + trackWidth, minHeight: rows.height + AXIS }} className="relative">
          {/* ---- time and acts, along the top ---- */}
          <div className="sticky top-0 z-30 flex border-b border-neutral-200/80 bg-white" style={{ height: AXIS }}>
            <div
              className="sticky left-0 z-10 flex flex-shrink-0 items-end border-r border-neutral-200/80 bg-white px-3 pb-2 text-[11px] text-neutral-400"
              style={{ width: labelWidth }}
            >
              {plural(seats.length, 'post')}
            </div>
            <div className="relative" style={{ width: trackWidth }}>
              {ticks.map((tick) => (
                <span
                  key={tick.day}
                  className={`absolute bottom-1.5 -translate-x-1/2 whitespace-nowrap tabular-nums ${
                    tick.major ? 'text-[11.5px] text-ink' : 'text-[10.5px] text-neutral-400'
                  }`}
                  style={{ left: xOf(tick.day) }}
                >
                  {tick.label}
                </span>
              ))}
              {view.acts.map((act) => (
                <ActMark
                  key={act.id}
                  act={act}
                  x={xOf(dayOf(act.date))}
                  active={selectedAct?.id === act.id}
                  onClick={() => onSelect(selectedAct?.id === act.id ? null : { kind: 'act', id: act.id })}
                />
              ))}
              <span
                className="absolute top-1.5 -translate-x-1/2 rounded-full bg-primary-600 px-1.5 text-[10px] leading-4 text-white"
                style={{ left: xOf(today) }}
              >
                Today
              </span>
            </div>
          </div>

          {/* ---- lines behind the bars: years, acts, today, one person's path ---- */}
          <svg
            className="pointer-events-none absolute z-0"
            style={{ left: labelWidth, top: AXIS }}
            width={trackWidth}
            height={rows.height}
          >
            {ticks
              .filter((tick) => tick.major)
              .map((tick) => (
                <line key={tick.day} x1={xOf(tick.day)} x2={xOf(tick.day)} y1={0} y2={rows.height} stroke="#f0f0f0" />
              ))}
            {view.acts.map((act) => {
              const x = xOf(dayOf(act.date));
              const active = selectedAct?.id === act.id;
              return (
                <line
                  key={act.id}
                  x1={x}
                  x2={x}
                  y1={0}
                  y2={rows.height}
                  stroke={active ? '#dc2626' : act.complete ? '#d4d4d4' : '#ececec'}
                  strokeWidth={active ? 1.5 : 1}
                  strokeDasharray={act.complete || active ? undefined : '3 4'}
                />
              );
            })}
            <line x1={xOf(today)} x2={xOf(today)} y1={0} y2={rows.height} stroke="#dc2626" strokeOpacity={0.5} />
            {curves.map((curve, index) => {
              const bend = Math.max(24, Math.abs(curve.x2 - curve.x1) / 2);
              return (
                <g key={index}>
                  <path
                    d={`M ${curve.x1} ${curve.y1} C ${curve.x1 + bend} ${curve.y1}, ${curve.x2 - bend} ${curve.y2}, ${curve.x2} ${curve.y2}`}
                    fill="none"
                    stroke="#dc2626"
                    strokeWidth={1.5}
                    strokeDasharray="4 3"
                  />
                  <circle cx={curve.x1} cy={curve.y1} r={2.5} fill="#dc2626" />
                  <circle cx={curve.x2} cy={curve.y2} r={2.5} fill="#dc2626" />
                </g>
              );
            })}
          </svg>

          {/* ---- the posts ---- */}
          {rows.list.map((row) =>
            row.type === 'section' ? (
              <div key={`section-${row.tier}`} className="flex" style={{ height: row.height }}>
                <div
                  className="sticky left-0 z-20 flex flex-shrink-0 items-end border-r border-neutral-200/80 bg-neutral-50/95 px-3 pb-1.5 text-[10.5px] uppercase tracking-[0.07em] text-neutral-400"
                  style={{ width: labelWidth }}
                >
                  <span className="truncate">{TIER_LABEL[row.tier]}</span>
                </div>
                <div className="bg-neutral-50/60" style={{ width: trackWidth }} />
              </div>
            ) : (
              <div key={row.seat.key} className="group flex border-b border-neutral-100/80" style={{ height: row.height }}>
                {(() => {
                  // A body that no longer exists: under the one that replaced it
                  const gone = row.seat.gone ?? row.seat.ended;
                  return (
                    <div
                      className={`sticky left-0 z-20 flex flex-shrink-0 flex-col justify-center border-r border-neutral-200/80 px-3 group-hover:bg-neutral-50 ${
                        row.seat.gone ? 'bg-neutral-50/95 pl-6' : 'bg-white'
                      }`}
                      style={{ width: labelWidth }}
                      title={`${postLabel(row.seat.post)} · ${row.seat.label}${gone ? ` — ${goneSentence(gone)}` : ''}`}
                    >
                      <span className="flex min-w-0 items-center gap-1.5">
                        {row.seat.gone && <CornerDownRight className="h-3 w-3 flex-shrink-0 text-neutral-300" strokeWidth={1.75} />}
                        <span className={`truncate text-[12px] leading-tight ${gone ? 'text-neutral-500' : 'text-ink'}`}>
                          {row.seat.label}
                        </span>
                        {gone && (
                          <span className="flex-shrink-0 rounded-full bg-neutral-200/70 px-1.5 py-px text-[9.5px] uppercase tracking-[0.05em] text-neutral-500">
                            Defunct
                          </span>
                        )}
                      </span>
                      <span className="truncate text-[10.5px] leading-tight text-neutral-400">
                        {gone ? goneSentence(gone) : postLabel(row.seat.post)}
                      </span>
                    </div>
                  );
                })()}
                <div className="relative" style={{ width: trackWidth }}>
                  {/* From the day it ended, the body is no more */}
                  {(row.seat.gone ?? row.seat.ended)?.endedOn && (
                    <div
                      aria-hidden
                      className="absolute inset-y-0"
                      style={{
                        left: xOf(dayOf((row.seat.gone ?? row.seat.ended)!.endedOn!)),
                        right: 0,
                        background:
                          'repeating-linear-gradient(135deg, transparent, transparent 6px, rgba(0,0,0,0.035) 6px, rgba(0,0,0,0.035) 8px)',
                      }}
                    />
                  )}
                  {row.seat.tenures.map((index, position) => {
                    const tenure = view.tenures[index];
                    const from = dayOf(tenure.from);
                    const to = tenure.to ? dayOf(tenure.to) : today;
                    const left = xOf(from);
                    const barWidth = Math.max(5, xOf(to) - left);
                    const tone = toneOf(tenure, row.seat.tier, colourBy);
                    const isSelected = selected?.kind === 'tenure' && selected.index === index;
                    const on = lit(index);
                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onSelect(isSelected ? null : { kind: 'tenure', index });
                        }}
                        onMouseEnter={(event) => setHovered({ index, x: event.clientX, y: event.clientY })}
                        onMouseMove={(event) => setHovered({ index, x: event.clientX, y: event.clientY })}
                        onMouseLeave={() => setHovered(null)}
                        aria-label={`${tenure.person}, ${tenure.title}, ${govDate(tenure.from)} to ${
                          tenure.to ? govDate(tenure.to) : 'today'
                        }`}
                        className={`absolute z-[1] flex items-center overflow-hidden rounded-md text-left transition-[opacity,box-shadow] duration-150 ${
                          isSelected ? 'ring-2 ring-primary-600 ring-offset-1' : 'hover:ring-1 hover:ring-black/20'
                        }`}
                        style={{
                          left,
                          width: barWidth,
                          top: ROW_PAD + row.lane[position] * LANE,
                          height: LANE - 6,
                          opacity: dimming && !on ? 0.22 : 1,
                          background: tenure.acting
                            ? `repeating-linear-gradient(135deg, ${tone.fill}, ${tone.fill} 5px, #ffffff 5px, #ffffff 8px)`
                            : tenure.openStart
                              ? `linear-gradient(90deg, ${tone.fill}00, ${tone.fill} 28px)`
                              : tone.fill,
                          boxShadow: `inset ${tenure.openStart ? 0 : 3}px 0 0 ${tone.edge}`,
                          color: tone.ink,
                        }}
                      >
                        {barWidth > 44 && (
                          <span className="truncate pl-2 pr-1.5 text-[11px] leading-none">
                            {tenure.person}
                            {barWidth > 190 && (
                              <span className="opacity-60"> · {lengthOf(tenure.from, tenure.to)}</span>
                            )}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ),
          )}
          {!rows.list.length && (
            <p className="px-4 py-10 text-center text-[13px] text-neutral-400">No post at this depth.</p>
          )}
        </div>
      </div>

        {/* ---- moving about ---- */}
        <div
          role="toolbar"
          aria-label="Timeline controls"
          className="absolute bottom-3 left-3 z-40 inline-flex items-center gap-0.5 rounded-full bg-white p-1 shadow-[0_8px_28px_-12px_rgba(0,0,0,0.22)] ring-1 ring-black/[0.06]"
        >
          <IconButton label="Zoom in" size="sm" onClick={() => zoom(1.5)}>
            <Plus className="h-4 w-4" strokeWidth={1.75} />
          </IconButton>
          <IconButton label="Zoom out" size="sm" onClick={() => zoom(1 / 1.5)}>
            <Minus className="h-4 w-4" strokeWidth={1.75} />
          </IconButton>
          <IconButton label="The whole period" size="sm" onClick={() => setPx(null)}>
            <Scan className="h-4 w-4" strokeWidth={1.75} />
          </IconButton>
          <IconButton label="Go to today" size="sm" onClick={() => goTo(today)}>
            <CalendarClock className="h-4 w-4" strokeWidth={1.75} />
          </IconButton>
        </div>
      </div>

      {/* ---- what is under the pointer ---- */}
      {hoveredTenure && hovered && (
        <div
          className="pointer-events-none fixed z-50 w-64 rounded-xl bg-white px-3 py-2.5 text-[12px] shadow-[0_12px_32px_-12px_rgba(0,0,0,0.3)] ring-1 ring-black/[0.06]"
          style={{
            left: Math.min(hovered.x + 14, window.innerWidth - 272),
            top: Math.min(hovered.y + 14, window.innerHeight - 140),
          }}
        >
          <p className="text-[13px] text-ink">{hoveredTenure.person}</p>
          <p className="mt-0.5 leading-snug text-neutral-500">{hoveredTenure.title}</p>
          <p className="mt-1.5 tabular-nums text-neutral-600">
            {hoveredTenure.openStart ? 'In post by ' : ''}
            {govDate(hoveredTenure.from)} – {hoveredTenure.to ? govDate(hoveredTenure.to) : 'today'}
            <span className="text-neutral-400"> · {lengthOf(hoveredTenure.from, hoveredTenure.to)}</span>
          </p>
          {hoveredTenure.confirmedBy.length > 0 && (
            <p className="mt-0.5 text-neutral-400">
              Kept {hoveredTenure.confirmedBy.length === 1 ? 'once' : `${hoveredTenure.confirmedBy.length} times`}
            </p>
          )}
        </div>
      )}

      {/* ---- what it says, and how to read it ---- */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-neutral-200/70 px-3.5 py-2.5 text-[11.5px] text-neutral-500">
        <span className="text-ink">{plural(inPost, 'holder')} in post</span>
        <span>{plural(view.acts.length, 'act')}</span>
        {legend.length > 0 ? (
          legend.map((entry) => (
            <span key={entry.label} className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-4 rounded-sm" style={{ background: entry.tone.fill, boxShadow: `inset 2px 0 0 ${entry.tone.edge}` }} />
              {entry.label}
            </span>
          ))
        ) : (
          <span>One colour per person — choose a bar to follow them from post to post</span>
        )}
        <span className="inline-flex items-center gap-1.5">
          <span
            className="h-2.5 w-4 rounded-sm"
            style={{ background: 'repeating-linear-gradient(135deg, #e5e5e5, #e5e5e5 3px, #fff 3px, #fff 5px)' }}
          />
          Acting
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-4 rounded-sm" style={{ background: 'linear-gradient(90deg, #e5e5e500, #e5e5e5)' }} />
          Already in post before the first act on file
        </span>
        <span className="ml-auto flex items-center gap-2">
          <Menu
            trigger={(props) => (
              <Button size="xs" variant="secondary" {...props}>
                <Palette className="h-3.5 w-3.5" strokeWidth={1.75} />
                {COLOURS.find((option) => option.value === colourBy)?.label}
              </Button>
            )}
            items={COLOURS.map((option) => ({
              label: option.label,
              icon: colourBy === option.value ? Check : undefined,
              onSelect: () => setColourBy(option.value),
            }))}
          />
          <span className="hidden text-neutral-400 lg:inline">Drag to move · ⌘ or Ctrl and scroll to zoom</span>
        </span>
      </div>
    </div>
  );
};

/** An act on the time axis: its kind's colour; a whole government stands taller */
const ActMark: React.FC<{ act: GovActView; x: number; active: boolean; onClick: () => void }> = ({
  act,
  x,
  active,
  onClick,
}) => {
  const kind = ACT_KIND[act.kind] ?? ACT_KIND.other;
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${govDate(act.date)} · ${act.title}`}
      aria-label={`${govDate(act.date)}, ${act.title}`}
      className="group/act absolute top-[22px] flex h-5 w-5 -translate-x-1/2 items-center justify-center"
      style={{ left: x }}
    >
      <span
        className={`block transition-transform group-hover/act:scale-125 ${act.complete ? 'h-3 w-3 rotate-45 rounded-[3px]' : 'h-2 w-2 rounded-full'} ${
          active ? 'ring-2 ring-primary-600 ring-offset-1' : ''
        } ${act.status !== 'read' ? 'opacity-40' : ''}`}
        style={{ backgroundColor: kind.colour }}
      />
    </button>
  );
};
